import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';

import CloseX from './close-x';
import Spinner from './spinner';

const useStyles = makeStyles((theme) => ({
  dialog: {
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
  },
  buttons: {
    width: 'auto',
    padding: '10px',
    margin: '17px',
  },
  cancelButton: {
    background: '#8DA8B2',
  },
  actionButton: {
    background: theme.palette.secondary.main,
  },
  title: { paddingTop: '50px' },
}));

type Props = {
  setShowModal: (value: boolean) => void;
  onActionClick?: () => void;
  onExitClick?: () => void;
  showModal: boolean;
  title?: string | React.ReactNode;
  content: string | JSX.Element;
  actionVerb?: string;
  cancelVerb?: string;
  showActions?: boolean;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | undefined;
  loading?: boolean;
  actionDisabled?: boolean;
  hideSecondaryButton?: boolean;
  signatureModal?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrideDialogStyles?: any | undefined; // override default MUI styles with provided styles
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrideDialogContentStyles?: any | undefined; // override default MUI styles with provided styles
  dialogActions?: React.ReactNode;
};

const Modal = ({
  setShowModal,
  onActionClick,
  onExitClick,
  showModal,
  content,
  title,
  actionVerb,
  cancelVerb = 'Cancel',
  showActions = true,
  maxWidth = 'sm',
  loading = false,
  actionDisabled = false,
  hideSecondaryButton = false,
  signatureModal = false,
  overrideDialogStyles = undefined,
  overrideDialogContentStyles = undefined,
  dialogActions,
}: Props) => {
  const classes = useStyles();

  return (
    <Dialog
      fullWidth={true}
      maxWidth={maxWidth}
      open={showModal}
      transitionDuration={{ enter: 300, exit: 0 }}
      onClose={() => {
        setShowModal(false);
      }}
      className={classes.dialog}
      style={overrideDialogStyles}
    >
      {title ? (
        <DialogTitle className={classes.title}>
          {' '}
          {title} <CloseX onClick={onExitClick} />{' '}
        </DialogTitle>
      ) : (
        <CloseX onClick={onExitClick} />
      )}
      <DialogContent style={overrideDialogContentStyles}>
        {loading && <Spinner />}
        {!loading && content}
      </DialogContent>
      {dialogActions && <DialogActions className={classes.actions}>{dialogActions}</DialogActions>}
      {showActions && (
        <DialogActions className={classes.actions}>
          {onActionClick && (
            <Button
              className={clsx(classes.buttons, classes.actionButton)}
              fullWidth
              disabled={actionDisabled}
              onClick={onActionClick}
              id="modal-action-button"
            >
              {actionVerb && actionVerb}
            </Button>
          )}
          {onExitClick && !signatureModal && !hideSecondaryButton && (
            <Button
              className={clsx(classes.buttons, classes.cancelButton)}
              fullWidth
              onClick={onExitClick}
              id="modal-cancel-button"
            >
              {cancelVerb}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
